/** Shopify integration panel with App Store and internal development-store flows. */
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  ShoppingBag,
  TrendingUp,
  Package,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Clock,
} from "lucide-react";

type ShopifyStatus = {
  connected: boolean;
  shopDomain?: string;
  shopName?: string;
  appStoreListingUrl?: string | null;
  canSelfServeInstall: boolean;
};

type ShopifyConnectPanelProps = {
  onInsightsLoaded?: (context: string) => void;
  forUserId?: string;
};

type ShopifyTopProduct = {
  title?: string | null;
  quantity?: number | string | null;
  revenue?: number | string | null;
};

type ShopifyInsightsResponse = {
  context: string;
  summary?: {
    totalProducts: number;
    activeProducts: number;
    totalOrders: number;
    totalRevenue: number | string;
    avgOrderValue: number | string;
    currency?: string | null;
    topProducts?: ShopifyTopProduct[];
  };
};

const ShopifyLogo = () => (
  <div className="w-6 h-6 rounded bg-[#95BF47] flex items-center justify-center">
    <ShoppingBag className="w-3.5 h-3.5 text-white" />
  </div>
);

export default function ShopifyConnectPanel({
  onInsightsLoaded,
  forUserId,
}: ShopifyConnectPanelProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [insightsData, setInsightsData] = useState<ShopifyInsightsResponse | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [shopDomain, setShopDomain] = useState("");
  const fuParam = forUserId ? `?${new URLSearchParams({ forUserId }).toString()}` : "";

  const { data: status, isLoading } = useQuery<ShopifyStatus>({
    queryKey: ["shopify-integration-status", forUserId],
    queryFn: () => apiRequest("GET", `/api/integrations/shopify/status${fuParam}`),
    staleTime: 30_000,
  });

  const disconnectMutation = useMutation({
    mutationFn: () =>
      apiRequest(
        "POST",
        "/api/integrations/shopify/disconnect",
        forUserId ? { forUserId } : undefined
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopify-integration-status"] });
      setInsightsData(null);
    },
  });

  const insightsMutation = useMutation({
    mutationFn: async () =>
      (await apiRequest(
        "POST",
        "/api/integrations/shopify/insights",
        forUserId ? { forUserId } : undefined
      )) as ShopifyInsightsResponse,
    onSuccess: (data) => {
      setInsightsData(data);
      onInsightsLoaded?.(data.context);
    },
  });

  const installMutation = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams({ shop: shopDomain });
      if (forUserId) params.set("forUserId", forUserId);
      return (await apiRequest(
        "GET",
        `/api/integrations/shopify/oauth/install?${params.toString()}`
      )) as { url: string };
    },
    onSuccess: ({ url }) => window.location.assign(url),
    onError: (error) => {
      toast({
        title: t("connectPanels.shopify.brand"),
        description:
          error instanceof Error ? error.message : t("connectPanels.shopify.installError"),
        variant: "destructive",
      });
    },
  });

  const startDevelopmentStoreInstall = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!shopDomain.trim()) return;
    installMutation.mutate();
  };

  const listingUrl = status?.appStoreListingUrl ?? null;
  const summary = insightsData?.summary;
  const topProducts = summary?.topProducts ?? [];

  const [pixelSnippetLoading, setPixelSnippetLoading] = useState(false);
  async function copyPixelSnippet() {
    setPixelSnippetLoading(true);
    try {
      const res = (await apiRequest("GET", "/api/integrations/shopify/custom-pixel-snippet")) as {
        snippet: string;
      };
      await navigator.clipboard.writeText(res.snippet);
      toast({
        title: t("connectPanels.shopify.brand"),
        description: t("connectPanels.shopify.pixelCopied"),
      });
    } catch (err) {
      toast({
        title: t("connectPanels.shopify.brand"),
        description:
          err instanceof Error ? err.message : t("connectPanels.shopify.pixelFetchError"),
        variant: "destructive",
      });
    } finally {
      setPixelSnippetLoading(false);
    }
  }

  if (isLoading) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-green-400" />
        <span className="text-sm text-white/60">{t("connectPanels.shopify.checking")}</span>
      </div>
    );
  }

  if (!status?.connected) {
    return (
      <div className="p-3 bg-white/[0.04] border border-white/[0.08] rounded-lg space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShopifyLogo />
            <span className="text-sm font-medium text-white">
              {t("connectPanels.shopify.brand")}
            </span>
            <Badge variant="outline" className="text-xs border-white/[0.1] text-white/60">
              {t("connectPanels.common.notConnected")}
            </Badge>
          </div>
        </div>
        {listingUrl ? (
          <>
            <p className="text-xs text-white/50">
              {t("connectPanels.shopify.appStoreDescription")}
            </p>
            <Button
              asChild
              size="sm"
              className="bg-[#95BF47] hover:bg-[#7ea73d] text-white text-xs"
            >
              <a href={listingUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3 w-3 mr-1" />
                {t("connectPanels.shopify.installFromAppStore")}
              </a>
            </Button>
          </>
        ) : (
          <>
            <p className="text-xs text-white/50">
              {t("connectPanels.shopify.approvalPendingDescription")}
            </p>
            <Button size="sm" disabled className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {t("connectPanels.shopify.approvalPendingButton")}
            </Button>
            <p className="text-[11px] text-white/40">{t("connectPanels.shopify.pilotHint")}</p>
          </>
        )}
        {/* VP-317: karta Shopify jedzie kontekstem konta zalogowanego (claim sklepu nie
            zna klienta agencji), więc agencja z auto-wybranym klientem musi wiedzieć, na
            czyje konto podepnie sklep — inaczej podpina „w ciemno" i nadpisuje własną
            integrację. Wybór klienta docelowego to osobne zadanie. */}
        <p className="text-[11px] text-white/40">{t("connectPanels.shopify.ownAccountScope")}</p>
        {status?.canSelfServeInstall && (
          <form
            onSubmit={startDevelopmentStoreInstall}
            className="space-y-2 border-t border-white/[0.08] pt-3"
          >
            <div>
              <p className="text-xs font-medium text-white/80">
                {t("connectPanels.shopify.developmentStoreTitle")}
              </p>
              <p className="text-[11px] text-white/40">
                {t("connectPanels.shopify.developmentStoreHint")}
              </p>
            </div>
            <div className="flex gap-2">
              <Input
                value={shopDomain}
                onChange={(event) => setShopDomain(event.target.value)}
                placeholder={t("connectPanels.shopify.shopDomainPlaceholder")}
                aria-label={t("connectPanels.shopify.shopDomainLabel")}
                className="h-8 text-xs"
                autoComplete="off"
              />
              <Button
                type="submit"
                size="sm"
                disabled={!shopDomain.trim() || installMutation.isPending}
                className="text-xs"
              >
                {installMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  t("connectPanels.shopify.startInstall")
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="p-3 bg-white/[0.04] border border-[#95BF47]/30 rounded-lg space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShopifyLogo />
          <span className="text-sm font-medium text-white">{t("connectPanels.shopify.brand")}</span>
          <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
            {t("connectPanels.common.connected")} · {status.shopName || status.shopDomain}
          </Badge>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => disconnectMutation.mutate()}
          disabled={disconnectMutation.isPending}
          className="text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
        >
          {t("connectPanels.common.disconnect")}
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => insightsMutation.mutate()}
          disabled={insightsMutation.isPending}
          className="bg-[#95BF47] hover:bg-[#7ea73d] text-white text-xs"
        >
          {insightsMutation.isPending ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" /> {t("connectPanels.common.fetching")}
            </>
          ) : (
            t("connectPanels.common.fetch")
          )}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => copyPixelSnippet()}
          disabled={pixelSnippetLoading}
          className="text-xs"
        >
          {pixelSnippetLoading ? (
            <>
              <Loader2 className="h-3 w-3 animate-spin mr-1" />
              {t("connectPanels.shopify.pixelFetching")}
            </>
          ) : (
            t("connectPanels.shopify.copyPixel")
          )}
        </Button>
      </div>
      <p className="text-[11px] text-white/40">{t("connectPanels.shopify.pixelInstructions")}</p>

      {summary && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5 text-green-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.products")}
                </span>
              </div>
              <div className="text-xl font-bold text-green-400 mt-1">{summary.totalProducts}</div>
              <span className="text-[10px] text-white/40">
                {t("connectPanels.shopify.activeProducts", {
                  count: summary.activeProducts,
                })}
              </span>
            </div>
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-3.5 w-3.5 text-blue-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.orders")}
                </span>
              </div>
              <div className="text-xl font-bold text-blue-400 mt-1">{summary.totalOrders}</div>
            </div>
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.common.revenue")}
                </span>
              </div>
              <div className="text-xl font-bold text-emerald-400 mt-1">
                {(Number(summary.totalRevenue) || 0).toFixed(0)} {summary.currency ?? ""}
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-3.5 w-3.5 text-amber-400" />
                <span className="text-[11px] text-white/60 uppercase">
                  {t("connectPanels.shopify.avgOrder")}
                </span>
              </div>
              <div className="text-xl font-bold text-amber-400 mt-1">
                {(Number(summary.avgOrderValue) || 0).toFixed(0)} {summary.currency ?? ""}
              </div>
            </div>
          </div>

          {topProducts.length > 0 && (
            <div>
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="flex items-center gap-1.5 text-xs text-white/60 hover:text-white/70 transition-colors w-full"
              >
                {showDetails ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                <span className="font-medium">
                  {t("connectPanels.shopify.topProducts", {
                    count: Math.min(topProducts.length, 10),
                  })}
                </span>
              </button>
              {showDetails && (
                <div className="mt-2 rounded-lg border border-white/[0.08] overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#0a0c10]/80 text-white/60">
                        <th className="text-left p-2 font-medium">
                          {t("connectPanels.common.product")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.sold")}
                        </th>
                        <th className="text-right p-2 font-medium">
                          {t("connectPanels.common.revenue")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topProducts.slice(0, 10).map((p: ShopifyTopProduct, i: number) => {
                        const revenue = Number(p.revenue) || 0;
                        const quantity = Number(p.quantity) || 0;
                        return (
                          <tr key={i} className="border-t border-white/[0.06]">
                            <td className="p-2 text-white max-w-[200px] truncate">
                              {p.title ?? "—"}
                            </td>
                            <td className="p-2 text-right text-blue-400">{quantity}</td>
                            <td className="p-2 text-right text-emerald-400">
                              {revenue.toFixed(0)} {summary.currency ?? ""}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex items-center gap-2">
            <Badge className="text-xs bg-green-600/20 text-green-400 border-green-500/50">
              {t("connectPanels.common.dataLoaded")}
            </Badge>
            <span className="text-xs text-white/40">
              {t("connectPanels.shopify.contextAttached")}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
